import { ComponentEntry, GetCatalogMarkdownOptions, PropDef } from './types';
import { CATALOG } from './catalog';

const CATEGORY_LABELS: Record<string, string> = {
  layout: 'Layout Components',
  display: 'Display Components',
  interactive: 'Interactive Components',
  domain: 'Domain Components (Center Platform)',
  organism: 'Metaspace Organisms',
};

const CATEGORY_ORDER = ['layout', 'display', 'interactive', 'domain', 'organism'];

/**
 * Cross-links between generated surface artifacts (EAINT-843). `space/v1` Space
 * documents supersede legacy `layout[]` metaspaces as the authoring format, so
 * each artifact points authors at its counterpart instead of leaving them to
 * discover the newer/older format on their own.
 */
const SURFACE_FOOTNOTES: Partial<Record<GetCatalogMarkdownOptions['surface'], string>> = {
  metaspace: 'See also `space.md` — the `space/v1` Space document format supersedes bare `layout[]` authoring for new metaspace templates.',
  space: 'See also `metaspace-organisms.md` — these organisms remain renderable inside a `space/v1` document via the `SpaceGrid` adapter.',
};

function renderPropType(prop: PropDef): string {
  if (prop.enum) {
    return prop.enum.map((v) => `"${v}"`).join(' | ');
  }
  if (prop.description) {
    return `${prop.type} (${prop.description.replace(/\.$/, '')})`;
  }
  return prop.type;
}

const ROLE_GRAMMAR = [
  '#### Component roles',
  '',
  'Every component declares a `role` describing its claim on canvas real estate:',
  '',
  '- **surface** — a full-region rendering family a Space hosts exactly one of (the `SpaceGrid`/`ContextShell` root itself, or a primary content organism like `Atlas`, `TreeMap`, `ArmillaryView`, `Users`, `Profile`).',
  '- **section** — a grid-placeable module docked around a Surface (`task-list`, `Scorecard`, companion panels like `ArmillaryActions`/`Preferences`, the `Section` container itself).',
  '- **primitive** — display/input vocabulary composed INSIDE a section (`Text`, `Button`, `Chart`, `SphereCard`, layout containers).',
  '',
  'Author a Space by picking exactly one **surface**, placing **sections** around it, and composing each section from **primitives**.',
  '',
].join('\n');

function renderComponent(entry: ComponentEntry): string {
  const lines: string[] = [];
  lines.push(`**${entry.type}** (role: ${entry.role}) — ${entry.description}`);
  for (const [name, prop] of Object.entries(entry.props)) {
    lines.push(`- \`${name}\`: ${renderPropType(prop)}`);
  }
  if (entry.dependencies) {
    lines.push('', `Dependencies: ${entry.dependencies}`);
  }
  if (entry.whenToUse) {
    lines.push('', entry.whenToUse);
  }
  if (entry.examples && entry.examples.length > 0) {
    lines.push('', 'Examples:');
    for (const example of entry.examples) {
      lines.push(`- \`${example}\``);
    }
  }
  return lines.join('\n');
}

export function getCatalogMarkdown(options: GetCatalogMarkdownOptions): string {
  const { surface } = options;
  const filtered = CATALOG.filter((e) => e.surfaces.includes(surface));

  const grouped = new Map<string, ComponentEntry[]>();
  for (const entry of filtered) {
    const cat = entry.category;
    if (!grouped.has(cat)) grouped.set(cat, []);
    grouped.get(cat)!.push(entry);
  }

  const sections: string[] = [ROLE_GRAMMAR];
  for (const category of CATEGORY_ORDER) {
    const entries = grouped.get(category);
    if (!entries || entries.length === 0) continue;
    const label = CATEGORY_LABELS[category] ?? `${category} Components`;
    sections.push(`#### ${label}`, '');
    for (const entry of entries) {
      sections.push(renderComponent(entry), '');
    }
  }

  const footnote = SURFACE_FOOTNOTES[surface];
  if (footnote) {
    sections.push('---', '', footnote, '');
  }

  return sections.join('\n').trim();
}
